/**
 * The privacy policy and the terms, as real routes.
 *
 * They exist because Google will not let an OAuth app leave testing without
 * stable URLs for both, but that is the deadline rather than the reason. A
 * game that has just started asking people for an email address owes them a
 * plain account of what happens to it.
 *
 * WRITTEN FROM THE CODE, NOT FROM A TEMPLATE. Every claim below was checked
 * against what the software does:
 *
 *   - nothing anywhere sets `document.cookie` or a `Set-Cookie` header
 *   - `ctx.ip` reaches `rateLimit` and no further; no table has a column for it
 *   - `telemetry.ts` states what a fault report carries, and it is the game
 *     state plus a user agent
 *   - `nohosts.test.ts` enforces that the client contacts no third-party host,
 *     and `scripts/e2e.ts` re-checks it against the built bundle
 *
 * That last one is the unusual claim and the one worth keeping. It is not a
 * promise about intentions; it is a test that fails the build.
 *
 * IF ADVERTISING ARRIVES, THIS PAGE CHANGES FIRST. There is deliberately no
 * pre-authorising language here -- no "we may share data with partners" hedge
 * covering something that does not happen. A policy that describes a future
 * the product has not reached is how a policy stops being true.
 */

import { navigate } from '../lib/router';

/** The date these were last actually revised, not a render-time `new Date()`. */
const UPDATED = '25 August 2026';

/**
 * Where a question about either document should go.
 *
 * A placeholder until a mailbox exists. Publishing a personal address on a
 * page a stranger can read is a decision for whoever owns the address.
 */
const CONTACT = 'privacy@worldlygames.com';

function Legal({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <main className="gg-screen gg-scroll gg-legal">
      <header className="gg-hero">
        <h1 className="gg-title">{title}</h1>
        <p className="gg-subtitle">Last updated {UPDATED}</p>
      </header>

      <div className="gg-legal-body">{children}</div>

      <section className="gg-section gg-section-quiet">
        <button
          type="button"
          className="gg-btn"
          onClick={() => navigate({ name: 'home' })}
        >
          Back to the globe
        </button>
      </section>
    </main>
  );
}

export function PrivacyScreen(): React.JSX.Element {
  return (
    <Legal title="Privacy">
      <p className="gg-legal-lead">
        Worldly is a geography game. It keeps what it needs to run a match and remember your
        progress, and nothing else. You can play the whole game without giving us an email
        address or any other personal detail.
      </p>

      <h2>What we keep</h2>
      <ul>
        <li>
          <strong>A player id.</strong> A random identifier created the first time you play. It is
          not derived from anything about you.
        </li>
        <li>
          <strong>A display name.</strong> Whatever you type in the name box. If you type nothing
          we generate one. Other players in your match see it.
        </li>
        <li>
          <strong>Your game history.</strong> Matches played, rounds, the answers you gave, how
          long you took, scores and skill rating.
        </li>
        <li>
          <strong>An email address, only if you create an account.</strong> We use it to sign you
          in and for nothing else. We do not send marketing.
        </li>
        <li>
          <strong>Fault reports.</strong> If the game breaks we record what it was doing, along
          with your browser identification string and window size. No page contents, nothing you
          typed.
        </li>
        <li>
          <strong>The site that linked you here</strong>, as a hostname only, so we know where
          players come from. Not the full address of the page.
        </li>
      </ul>

      <h2>What we do not keep</h2>
      <ul>
        <li>
          <strong>No cookies.</strong> None at all. What the game remembers between visits lives
          in your own browser storage, and clearing it clears the game.
        </li>
        <li>
          <strong>No IP addresses.</strong> Your address is used to rate-limit requests while they
          happen, and is never written to our database.
        </li>
        <li>
          <strong>No advertising or analytics trackers.</strong> There is no Google Analytics, no
          advertising pixel, and no third-party script of any kind in the game.
        </li>
        <li>No location data, no contacts, no device fingerprinting.</li>
      </ul>

      <h2>While you are playing, nothing else is contacted</h2>
      <p>
        The map imagery, the flags and the country outlines are all served from us. During a
        round your browser talks to our server and to no one else. This is enforced by an
        automated check that fails our build if a third-party address is ever added.
      </p>

      <h2>Who else is involved</h2>
      <ul>
        <li>
          <strong>Google</strong>, only if you choose to sign in with it. Google tells us your
          email address and an identifier. We ask for nothing else, and we send them nothing
          about how you play.
        </li>
        <li>
          <strong>Amazon Web Services</strong>, which hosts the game and the database, and
          delivers your sign-in email. That email carries your address and the link, nothing
          else.
        </li>
        <li>
          <strong>SiteGround</strong>, which runs the mailbox behind the address at the bottom
          of this page. They see whatever you choose to write to us, and nothing you do not.
        </li>
      </ul>

      <h2>How long we keep it</h2>
      <p>
        Game history stays while your account does. Fault reports are deleted automatically after
        a short retention period. If you play without an account and clear your browser storage,
        we have no way to connect you to what is left and it simply becomes an orphaned record.
      </p>

      <h2>What you can do</h2>
      <ul>
        <li>Play without an account. Everything except cross-device progress works.</li>
        <li>Change your display name whenever you like.</li>
        <li>
          Ask us to delete your account and its history, by writing to{' '}
          <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. We will also tell you what we hold if you
          ask.
        </li>
      </ul>

      <h2>Children</h2>
      <p>
        Worldly is not directed at children under 13 and we do not knowingly create accounts for
        them. If you believe a child has created one, write to us and we will remove it.
      </p>

      <h2>If this changes</h2>
      <p>
        We may add advertising or other paid features in future. If we do, this page will say so
        before it happens, and the date at the top will change. We will not quietly start sharing
        your data with anyone under wording that was written today.
      </p>

      <h2>Contact</h2>
      <p>
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
      </p>
    </Legal>
  );
}

export function TermsScreen(): React.JSX.Element {
  return (
    <Legal title="Terms">
      <p className="gg-legal-lead">
        Worldly is a free game. These terms are short because there is not much to say: play
        fair, be reasonable about what you call yourself, and understand that we make no
        promises about a free game staying up.
      </p>

      <h2>Using it</h2>
      <p>
        You may play Worldly for free, with or without an account. You are responsible for what
        happens under your account, so keep access to your email secure, since that is how
        signing in works.
      </p>

      <h2>Fair play</h2>
      <ul>
        <li>Do not automate play, modify the client to gain an advantage, or attack the service.</li>
        <li>
          Do not choose a display name that is abusive, impersonates somebody, or that you would
          not want a stranger to read. Names are visible to other players.
        </li>
        <li>
          We may suspend or remove an account that does any of the above. For anything short of
          deliberate abuse we would rather write to you first.
        </li>
      </ul>

      <h2>Your account</h2>
      <p>
        You can ask us to delete your account and its history at any time by writing to{' '}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. We may close accounts that have been unused
        for a very long time, and would email you before doing so.
      </p>

      <h2>No warranty</h2>
      <p>
        Worldly is provided as it is, without warranty of any kind. We do not promise it will be
        available, that matches will be free of faults, or that your progress cannot be lost. It
        is a game, not a service you should depend on.
      </p>

      <h2>Liability</h2>
      <p>
        To the extent the law allows, we are not liable for any loss arising from using Worldly.
        Nothing here limits liability that cannot lawfully be limited.
      </p>

      <h2>The geography itself</h2>
      <p>
        Place names, borders and facts come from public datasets. Borders are shown as those
        datasets record them and are not a statement of any political position. If you find an
        error, please tell us.
      </p>

      <h2>Changes</h2>
      <p>
        We may update these terms. The date at the top changes when we do, and continuing to play
        means accepting the current version.
      </p>

      <h2>Contact</h2>
      <p>
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
      </p>
    </Legal>
  );
}
